import { ListNavigationProvider, useListPageNavigation } from '../../../../hooks/listNavigation';
import React, { useState, useCallback, useEffect } from 'react';
import { CustomErrorLogger } from '../../../../utils/CustomErrorLogger';
import { useHasPermission } from '../../../../hooks/useHasPermission';
import { Box, Typography, CircularProgress } from '@mui/material';
import MachineReportFiltration from '../machineReportFiltration';
import MachineReportListView from './machineReportListView';
import { AddReportDialoge } from '../addMachineReport';
import { useSelector } from 'react-redux';
import NoRecord from '../../../NoRecord';
import Iconify from '../../../Iconify';
import axios from 'axios';

const ReadMachineReportsTab = () => {

    const machineDetailsData = useSelector((state) => state.MachineDetailsData);

    const hasReportCreatePermission = useHasPermission('controls_create');

    const limit = 30;
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [totalReports, setTotalReports] = useState('');
    const [fetchedReports, setFetchedReports] = useState();
    const [openAddReport, setOpenAddReport] = useState(false);
    const [filteredReportsCount, setFilteredReportsCount] = useState(null);
    const [machineReportFiltration, setMachineReportFiltration] = useState(
        JSON.parse(localStorage.getItem('machineReportFiltration')) ||
        {
            search: '',
            date: '',
            status: '',
        });
    const [sortBy, setSortBy] = useState(
        JSON.parse(localStorage.getItem('MachineReportSortBy')) ||
        {
            controle_date: null,
            company: null,
            accompanist: null,
            status: null,
            machine: null,
        });
    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'machine-reports',
        pathname: String(machineDetailsData?.id || ''),
        displayMode: 'list',
        itemSizes: { list: 70 },
        resetOnFilters: machineReportFiltration,
        resetOnSort: sortBy,
        ready: !loading,
    });

    useEffect(() => {
        localStorage.setItem('machineReportFiltration', JSON.stringify(machineReportFiltration));
    }, [machineReportFiltration]);

    useEffect(() => {
        localStorage.setItem('MachineReportSortBy', JSON.stringify(sortBy));
    }, [sortBy]);

    const fetchReports = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('machine-control', { params: { offset: adjustedOffset, limit, filters: machineReportFiltration, machineId: machineDetailsData.id, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedReports = [];
                // Update the fetched records in their correct positions
                response.data.controlesData.forEach((file, index) => {
                    updatedReports[adjustedOffset + index] = file;
                });
                // Update the state with the new files array
                setLoading(false);
                setReports(updatedReports);
                setFilteredReportsCount(response.data.filteredControlesCount);
                setTotalReports(response.data.totalControlesCount);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching reports:', error);
            CustomErrorLogger(error, 'fetch reports in reports page');
        }
    }, [offset, machineDetailsData.id, machineReportFiltration, sortBy]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const handleSort = (field) => {
        setSortBy((prevSortBy) => {
            const nextSortOrder = prevSortBy[field] === null ? 'asc'
                : prevSortBy[field] === 'asc' ? 'desc'
                    : null;

            return Object.fromEntries(
                Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
            );
        });

    };

    const handleResetFilters = useCallback(() => {
        setMachineReportFiltration({
            search: '',
            company_id: '',
            date: '',
            isActive: false,
        });
    }, [setMachineReportFiltration])


    return (
        <ListNavigationProvider value={navigationValue}>
        <Box>
            {(hasReportCreatePermission && !machineDetailsData?.is_archived && totalReports > 0) &&
                <Box className='invoice_headre_action_box' sx={{ marginBottom: '10px' }} onClick={() => setOpenAddReport(true)}>
                    <Iconify className='invoice_headre_add_icon' icon={"ic:baseline-plus"} />
                    <Typography className='invoice_headre_action_title'>Ajouter un rapport</Typography>
                </Box>
            }
            {loading ?
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '80vh',
                    }}
                >
                    <CircularProgress
                        sx={{
                            '& .MuiCircularProgress-circle': { stroke: '#EA5E00' },
                        }}
                    />
                </Box>
                :
                totalReports > 0 ?
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <MachineReportFiltration
                            totalReports={totalReports}
                            fetchedReports={fetchedReports}
                            handleResetFilters={handleResetFilters}
                            filteredReportsCount={filteredReportsCount}
                            machineReportFiltration={machineReportFiltration}
                            setMachineReportFiltration={setMachineReportFiltration}
                        />
                        <MachineReportListView
                            fromMachinePage={true}
                            scrollRef={scrollRef}
                            setOffset={setOffset}
                            machineReportFiltration={machineReportFiltration}
                            setMachineReportFiltration={setMachineReportFiltration}
                            filteredReportsCount={filteredReportsCount}
                            setReportsFetched={setFetchedReports}
                            reports={reports}
                            sortBy={sortBy}
                            handleSort={handleSort}
                        />
                    </Box>
                    :
                    <NoRecord
                        description='Aucun rapport disponible pour cette machine'
                        buttonTitle='Ajouter un rapport'
                        handleOpenDialog={() => setOpenAddReport(true)}
                        hasPermission={hasReportCreatePermission && !machineDetailsData?.is_archived}
                    />
            }
            {openAddReport &&
                <AddReportDialoge
                    fromMachinePage={true}
                    openAddReport={openAddReport}
                    setOpenAddReport={setOpenAddReport}
                    machine_id={machineDetailsData.id}
                    machine_status={machineDetailsData.status}
                    setReports={setReports}
                    setTotalReports={setTotalReports}
                    setFilteredReportsCount={setFilteredReportsCount}
                />
            }
        </Box>
        </ListNavigationProvider>
    );
}

export default ReadMachineReportsTab;